import * as React from 'react';
import { Component, ComponentProps, deps, inject, observer } from '../lib/component';
import { PlayButtonCompat } from '../components/compat/play-button-compat';
import { AddRemoveCompat } from '../components/compat/add-remove-compat';
import { classes, style } from 'typestyle';
import { centerCenter } from 'csstips';
import { em, important, percent, px, viewHeight } from 'csx';
import { FavouriteButtonCompat } from '../components/compat/favourite-button-compat';
import { PageHomeJustAdded } from '../components/page-home-just-added';
import { PageHomeSections } from '../components/page-home-sections';
import { PageHomePricing } from '../components/page-home-pricing';
import { PricingCalculator } from '../components/pricing-calculator';
import { PageHomeCreators } from '../components/page-home-creators';
import { animationBounce } from '../theme/animation';
import { transitionQuickEase } from '../theme/transition';
import { colorContrast, colorSubtle, colorWhite } from '../theme/color';
import { mediaMobileOnly, mediaTablet, mediaDesktop, mediaUpToTablet } from '../theme/media';
import { zHeroContent } from '../theme/z';
import MainWrapper from '../components/main-wrapper';
import HeroSearch from '../components/hero/search';
import { GenericCarousel } from '../components/page-home-generic-carousel';
import DiscountImage from '../static/25_Off_Sticker.png';
import { PlaylistCarousel } from '../components/carousel/playlist-carousel';
import { TitledCarousel } from '../components/carousel/titled-carousel';
import { PaddedCarousel } from '../components/carousel/padded-carousel';
import { FavouriteArtistsCarousel, FavouriteTracksCarousel } from '../components/carousel/favourite-carousel';
import { FavouriteArtistButtonCompat } from '../components/compat/favourite-artist-button-compat';
import { isTrackArray, TrackSchema } from '../types/schema';
import { isObservableArray } from 'mobx';
import { FAVOURITE_CAROUSEL } from '../components/devtools/feature-flags';
import { GenericCarouselTracks } from '../components/page-home-generic-carousel-tracks';

@inject(deps)
@observer
export class HomePage extends Component<ComponentProps> {
  render() {
    const { model, controller } = this.props;
    const { home } = model.page;
    const { storage } = model;

    const { loading, favouriteTracks = [], favouriteArtists = [], carousels = [] } = home;

    const { preloadImage, preloadContent, preloadRaw } = this.props.controller.image;
    const { user } = this.props.model.auth;
    const { analytics } = this.props.controller;
    const { handleClickLink, handleClickSignIn } = this.props.controller.ui;

    const isAuthed = typeof user === 'object' && user !== null;
    const goToFavourites = () => model.router.push('/account/favourites');
    const overlayAction = isAuthed ? goToFavourites : handleClickSignIn;
    const showFavouriteCarousel = storage.getItem(FAVOURITE_CAROUSEL) === true;

    return (
      <MainWrapper>
        <div className={HomePage.styles.content}>
          <img src={DiscountImage} className={HomePage.styles.discountImg} alt="Get 25% off your first license" />
          <HeroSearch defaultSuggestions={home.heroSuggestions} />
          {showFavouriteCarousel && (
            <FavouriteTracksCarousel
              showOverlay={!isAuthed || favouriteTracks.length === 0}
              overlayAction={overlayAction}
              onClickLink={handleClickLink}
              tracks={favouriteTracks}
              preload={preloadImage}
              PlayButton={PlayButtonCompat}
              AddRemove={AddRemoveCompat}
              Favourite={FavouriteButtonCompat}
              analytics={analytics}
              hasBackground={true}
            />
          )}
          {showFavouriteCarousel && (
            <FavouriteArtistsCarousel
              showOverlay={!isAuthed || favouriteArtists.length === 0}
              overlayAction={overlayAction}
              onClickLink={handleClickLink}
              artists={favouriteArtists}
              preload={preloadImage}
            />
          )}
          {!loading &&
            carousels.map((carousel, index) => {
              if (!isObservableArray(carousel) || carousel.length === 0) return null;
              const { carousel_title: title, content_slug: slug } = model.content.homepageCarousel[index];
              if (index === 0 && !showFavouriteCarousel && isTrackArray(carousel)) {
                return (
                  <PageHomeJustAdded
                    key={index}
                    analytics={analytics}
                    model={carousel}
                    PlayButton={PlayButtonCompat}
                    AddRemove={AddRemoveCompat}
                    Favourite={FavouriteButtonCompat}
                    onClickLink={handleClickLink}
                    preload={preloadImage}
                    i18n={{ title }}
                    href={`/browse/collection/${slug}`}
                  />
                );
              } else if (isTrackArray(carousel)) {
                return (
                  <TitledCarousel
                    key={index}
                    i18n={{ title }}
                    href={`/browse/collection/${slug}`}
                    onClickLink={handleClickLink}
                  >
                    <GenericCarouselTracks
                      analytics={analytics}
                      tracks={carousel}
                      PlayButton={PlayButtonCompat}
                      AddRemove={AddRemoveCompat}
                      Favourite={FavouriteButtonCompat}
                      onClickLink={handleClickLink}
                      preload={preloadImage}
                    />
                  </TitledCarousel>
                );
              }

              return (
                <TitledCarousel key={index} i18n={{ title }} href="/browse/collection" onClickLink={handleClickLink}>
                  <PlaylistCarousel playlists={carousel} onClickLink={handleClickLink} />
                </TitledCarousel>
              );
            })}
          <PageHomeSections model={home} preload={preloadImage} preloadRaw={preloadRaw} />
          <PageHomePricing
            model={home}
            preload={preloadContent}
            onClickLink={handleClickLink}
            onClickShowCalculator={this.handleHidePricing}
            onSelectRateType={controller.page.home.selectRateType}
          >
            {!home.showPricingGrid && (
              <PricingCalculator
                analytics={analytics}
                className={HomePage.styles.pricingCalculator}
                model={model.pricing}
                inverted={true}
                onClickLink={handleClickLink}
                onSearch={controller.pricing.search}
                onSubmit={controller.pricing.submit}
                onChangeQuery={controller.pricing.changeQuery}
                onChangeCurrency={controller.pricing.changeCurrency}
                onSignUp={user ? void 0 : handleClickSignIn}
                onShowPricing={this.handleShowPricing}
                onSelectRateType={controller.pricing.selectRateType}
              />
            )}
          </PageHomePricing>
          <PageHomeCreators
            preload={preloadImage}
            model={home}
            i18n={{ title: home.creatorsTitle, view_cta: home.creatorsViewCTA }}
          />
        </div>
      </MainWrapper>
    );
  }

  handleShowPricing = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    this.props.controller.analytics.sendMixpanel('User clicks show pricing');
    this.props.controller.page.home.showPricingGrid();
  };

  handleHidePricing = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    this.props.controller.page.home.hidePricingGrid();
  };

  componentWillMount() {
    this.props.controller.pricing.reset();
  }

  componentWillUnmount() {
    this.props.controller.page.home.destroyCarousel();
    this.props.controller.page.home.disposer();
  }

  static styles = {
    content: style({
      zIndex: zHeroContent,
      position: 'relative',
      color: colorSubtle.toString(),
    }),
    firstFree: style(
      {
        ...centerCenter,
        padding: '40px 20px',
        $nest: {
          '& > div': {
            ...animationBounce,
            cursor: 'pointer',
            transition: `all ${transitionQuickEase}`,
            width: percent(100),
            padding: px(20),
            background: colorContrast.toString(),
            borderRadius: px(20),
            color: colorWhite.toString(),
          },
          '& > div:hover': {
            background: colorContrast.darken(0.15).toString(),
          },
          '& h5': {
            color: colorWhite.toString(),
            margin: 0,
            fontSize: px(24),
          },
          '& p': {
            margin: '20px 0 0',
            fontSize: px(20),
          },
        },
      },
      mediaTablet({
        margin: '40px auto',
        maxWidth: px(640),
        padding: '30px 60px',
        $nest: {
          '&> div': {
            padding: px(30),
          },
          '& h5': {
            color: colorWhite.toString(),
            margin: 0,
            fontSize: px(30),
          },
          '& p': {
            margin: '30px 0 0',
            fontSize: px(22),
          },
        },
      }),
    ),
    pricingCalculator: style(mediaTablet({ padding: important('0 80px') })),
    discountImg: style(
      {
        position: 'absolute',
        width: em(12),
        height: 'auto',
        right: em(1),
        top: em(1),
      },
      mediaMobileOnly({
        top: viewHeight(60),
        width: em(6),
      }),
    ),
  };
}
